import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent, ThemeEvents } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);

    const { dialog } = /** @type {Refs} */ (this.refs);
    if (dialog) {
      this.#resizeObserver = new ResizeObserver(() => this.#updateStickyState());
      this.#resizeObserver.observe(dialog);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }

  /**
   * Handles cart add - updates cart drawer content and opens drawer
   */
  #handleCartAdd = (event) => {
    if (!this.hasAttribute('auto-open')) return;

    // Directly morph the cart-drawer-component's dialog content from the sections response,
    // bypassing the full-section morph which doesn't reliably reach the cart content.
    const sectionsHtml = event.detail?.data?.sections;
    const cartItems = this.querySelector('cart-items-component');
    if (sectionsHtml && cartItems) {
      const sectionId = cartItems.dataset.sectionId;
      const html = sectionsHtml[sectionId];
      if (html) {
        const fragment = new DOMParser().parseFromString(html, 'text/html');
        const newDialog = fragment.querySelector('cart-drawer-component dialog');
        const { dialog } = /** @type {Refs} */ (this.refs);
        if (newDialog && dialog) {
          // Morph dialog element AND its children (not childrenOnly)
          morph(dialog, newDialog, { ...MORPH_OPTIONS, childrenOnly: false });
          this.#updateStickyState();
        }
      }
    }

    if (event.detail?.data?.source !== 'cart-auto-add') {
      this.showDialog();
    }
  };

  /**
   * Handles cart update - updates dialog state when cart becomes empty.
   * Only updates CSS classes; does NOT restructure the DOM (morph handles that).
   * @param {CustomEvent} event - The cart update event
   */
  #handleCartUpdate = (event) => {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    const itemCount = event.detail?.data?.itemCount;

    if (itemCount === 0) {
      dialog.classList.add('cart-drawer--empty');
      dialog.classList.remove('cart-drawer__dialog--with-cross-sell');
      requestAnimationFrame(() => this.#updateStickyState());
    } else if (itemCount > 0) {
      dialog.classList.remove('cart-drawer--empty');
    }
  };

  open() {
    this.showDialog();

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Defer measurement until layout is complete (dialog may be mid-animation or not yet laid out)
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const content = dialog.querySelector('.cart-drawer__content');
        const summary = dialog.querySelector('.cart-drawer__summary');

        if (!content || !summary) {
          dialog.setAttribute('cart-summary-sticky', 'false');
          return;
        }

        const drawerHeight = dialog.getBoundingClientRect().height;
        const summaryHeight = summary.getBoundingClientRect().height;
        const ratio = drawerHeight > 0 ? summaryHeight / drawerHeight : 0;
        dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
      });
    });
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

/**
 * Whether the cart drawer modal is currently open (for callers about to morph header/cart DOM).
 * @returns {boolean}
 */
export function getCartDrawerModalWasOpen() {
  const dialog = document.querySelector('cart-drawer-component dialog');
  return dialog instanceof HTMLDialogElement && dialog.open;
}

/**
 * Section HTML from cart AJAX does not include `open` on `<dialog>`; morphing the header section
 * copies attributes from that HTML and drops modal state. Call after morph when the drawer was open
 * (e.g. cart-auto-add gift/discount updates after add to cart).
 *
 * @param {boolean} wasOpen - Snapshot from {@link getCartDrawerModalWasOpen} before morph
 */
export function restoreCartDrawerModalIfWasOpen(wasOpen) {
  if (!wasOpen) return;
  const cartDrawer = document.querySelector('cart-drawer-component');
  if (!cartDrawer) return;
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const dialog = cartDrawer.querySelector('dialog');
      if (dialog instanceof HTMLDialogElement && !dialog.open) {
        cartDrawer.showDialog();
      }
    });
  });
}
